import React from "react";
import { useAuth } from "@/lib/auth";
import { LAUNCH_DATE, MAINTENANCE_MODE } from "@/lib/site-config";
import ComingSoon from "@/pages/ComingSoon";
import { Loader2, Eye, X } from "lucide-react";

const PREVIEW_BANNER_DISMISSED_KEY = "ll_preview_banner_dismissed";

/**
 * Wraps public pages.
 * - Until LAUNCH_DATE (and MAINTENANCE_MODE on): non-admins see ComingSoon.
 * - Authenticated admins (Infomaniak SSO + lalutrive-admins team) see the real page,
 *   with a small "preview" banner.
 */
export const MaintenanceGuard = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-brand-cream flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
            </div>
        );
    }

    const isLocked = MAINTENANCE_MODE && new Date() < LAUNCH_DATE;
    if (isLocked && !user) {
        return <ComingSoon />;
    }

    return (
        <>
            {isLocked && user && <PreviewBanner />}
            {children}
        </>
    );
};

const PreviewBanner = () => {
    const [hidden, setHidden] = React.useState(() => {
        try {
            return sessionStorage.getItem(PREVIEW_BANNER_DISMISSED_KEY) === "1";
        } catch (err) {
            console.debug("sessionStorage read failed", err?.message);
            return false;
        }
    });
    if (hidden) return null;
    return (
        <div className="fixed top-0 inset-x-0 z-[60] bg-brand-primary text-brand-cream text-xs font-sans" data-testid="preview-banner">
            <div className="max-w-[1280px] mx-auto px-4 py-2 flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2">
                    <Eye className="h-3.5 w-3.5" />
                    Mode aperçu — site visible uniquement pour l'équipe jusqu'au 1<sup>er</sup> juillet 2026.
                </span>
                <button
                    onClick={() => {
                        try {
                            sessionStorage.setItem(PREVIEW_BANNER_DISMISSED_KEY, "1");
                        } catch (err) {
                            console.debug("sessionStorage write failed", err?.message);
                        }
                        setHidden(true);
                    }}
                    aria-label="Fermer"
                    className="opacity-70 hover:opacity-100"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
};

export default MaintenanceGuard;
